import { type Extensions } from '@tiptap/core';
import { CharacterCount } from '@tiptap/extension-character-count';
import { Image as ImageExtension } from '@tiptap/extension-image';
import { StarterKit } from '@tiptap/starter-kit';
import { sum } from 'es-toolkit';
import { calculateBytesFromMB } from '../../common/calculation/calculate-bytes-in-mb';

export const MAX_TOTAL_IMAGES_SIZE_IN_MB = 20;

export const HEADING_LEVELS = [1, 2, 3, 4, 5, 6] as const;
export type HeadingLevel = (typeof HEADING_LEVELS)[number];

export const getDataUriImageSizeInBytes = (src: string): number => {
  const commaIndex = src.indexOf(',');

  if (commaIndex < 0 || !src.startsWith('data:image/')) {
    return 0;
  }

  try {
    return atob(src.slice(commaIndex + 1)).length;
  } catch {
    return 0;
  }
};

export const getHtmlImageSizesInBytes = (html: string): number[] => {
  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');

  return Array.from(parsedDocument.querySelectorAll('img'))
    .map(image => getDataUriImageSizeInBytes(image.getAttribute('src') ?? ''));
};

export const exceedsTotalImagesLimit = (existingHtml: string, incomingImageSizesInBytes: number[]): boolean => {
  const totalSizeInBytes = sum([...getHtmlImageSizesInBytes(existingHtml), ...incomingImageSizesInBytes]);

  return totalSizeInBytes > calculateBytesFromMB(MAX_TOTAL_IMAGES_SIZE_IN_MB);
};

export const buildRichTextExtensions = (options?: { maxLength?: number }): Extensions => [
  StarterKit.configure({
    heading: { levels: [...HEADING_LEVELS] },
    link: {
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
      HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
    },
  }),
  ImageExtension.configure({ allowBase64: true }),
  CharacterCount.configure(options?.maxLength !== undefined ? { limit: options.maxLength } : {}),
];
